import React from 'react';
import { Loader2, AlertTriangle, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Executive summary + enhanced risk descriptions from Gemini Pro.
 * Rendered as a plain-text block — markdown parsing happens via the
 * whitespace-pre-line class since the Pro narratives are short paragraphs.
 */
export default function NarrativeReport({ narrative, enhancedRisks, loading }) {
    if (loading) {
        return (
            <div className="flex items-center justify-center p-10 text-slate-500 dark:text-slate-400">
                <Loader2 className="animate-spin mr-2" size={16} />
                Generating executive summary…
            </div>
        );
    }

    if (!narrative && (!enhancedRisks || enhancedRisks.length === 0)) {
        return (
            <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-6 text-center text-sm text-slate-500 dark:text-slate-400">
                No narrative available. Open this tab to generate one.
            </div>
        );
    }

    return (
        <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-700">
            {enhancedRisks && enhancedRisks.length > 0 && (
                <section>
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle size={16} className="text-orange-600 dark:text-orange-400" />
                        <div className="text-xs uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Enhanced Risks</div>
                    </div>
                    <ol className="space-y-3 list-decimal list-inside">
                        {enhancedRisks.map((r, i) => (
                            <li key={i} className="text-sm leading-relaxed text-slate-700 dark:text-slate-200 pl-1">
                                <div className="inline prose prose-sm dark:prose-invert max-w-none [&_p]:inline">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{r}</ReactMarkdown>
                                </div>
                            </li>
                        ))}
                    </ol>
                </section>
            )}

            {narrative && (
                <section>
                    <div className="flex items-center gap-2 mb-3">
                        <FileText size={16} className="text-blue-600 dark:text-blue-400" />
                        <div className="text-xs uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Executive Summary</div>
                    </div>
                    <div className="prose prose-sm max-w-none dark:prose-invert text-slate-800 dark:text-slate-100 leading-relaxed
                                    prose-headings:tracking-tight prose-headings:font-semibold
                                    prose-p:leading-relaxed prose-strong:text-slate-900 dark:prose-strong:text-white
                                    prose-ol:my-2 prose-ul:my-2 prose-li:my-1">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{narrative}</ReactMarkdown>
                    </div>
                </section>
            )}
        </div>
    );
}
